// /src/components/Contact.tsx

import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

declare global { interface Window { grecaptcha?: any; } }

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string;
// Toggle to `true` temporarily if you want to see what entries go into FormData() in the browser console.
const CLIENT_DEBUG = false;

type FieldErrors = Partial<Record<"name"|"email"|"phone"|"message"|"captcha"|"eventDate"|"eventType"|"servings", string>>;
type FileEntry = { file: File };

export default function Contact() {
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: "", email: "", phone: "",
    eventDate: "", eventType: "", servings: "",
    location: "",
    message: "",
  });
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  // ⬇️ modal to show result of send
  const [showResult, setShowResult] = useState(false);
  const [resultOk, setResultOk] = useState<boolean | null>(null);
  const [resultMsg, setResultMsg] = useState<string>("");

  const formRef = useRef<HTMLFormElement|null>(null);
  const recaptchaDivRef = useRef<HTMLDivElement|null>(null);
  const widgetIdRef = useRef<number|null>(null);
  const pendingRef = useRef<typeof formData | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Mount invisible reCAPTCHA
  useEffect(() => {
    const mount = () => {
      if (!window.grecaptcha || !recaptchaDivRef.current || widgetIdRef.current !== null) return;
      window.grecaptcha.ready(() => {
        widgetIdRef.current = window.grecaptcha.render(recaptchaDivRef.current!, {
          sitekey: SITE_KEY,
          size: "invisible",
          badge: "bottomright",
          callback: (token: string) => { void doSubmit(token); },
          "expired-callback": () => {
            setSubmitting(false);
            toast({ title: "Verification expired. Please try again.", variant: "destructive" });
          },
          "error-callback": () => {
            // Submit anyway so users aren’t stuck if captcha isn’t configured
            setSubmitting(false);
            toast({ title: "Verification error, submitting anyway.", description: "We’ll still receive your message.", variant: "destructive" });
            void doSubmit("");
          }
        });
      });
    };
    if (window.grecaptcha) mount();
    else {
      const s = document.createElement("script");
      s.src = "https://www.google.com/recaptcha/api.js?render=explicit";
      s.async = true; s.defer = true; s.onload = mount;
      document.head.appendChild(s);
    }
  }, []);

  const onChange =
    (f: keyof typeof formData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.currentTarget.value;
      setFormData(p => ({ ...p, [f]: value }));
      if (f in errors) setErrors(prev => ({ ...prev, [f]: undefined }));
      setServerError(null);
    };

  const onFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    setServerError(null);
    const picked = e.target.files ? Array.from(e.target.files) : [];
    if (!picked.length) {
      setFiles([]);
      return;
    }
    const MAX_MB = 5;
    const accepted = picked.filter(f => f.size / (1024*1024) <= MAX_MB);
    if (accepted.length < picked.length) {
      toast({ title: "Some files were too large", description: `Max ${MAX_MB} MB each`, variant: "destructive" });
    }
    setFiles(accepted.map(file => ({ file })));
  };

  const removeAttachmentAt = (i: number) => {
    const next = files.filter((_, idx) => idx !== i);
    setFiles(next);
    if (next.length === 0 && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const validate = (s: typeof formData): FieldErrors => {
    const e: FieldErrors = {};
    const name = s.name.trim();
    const email = s.email.trim();
    const phone = s.phone.trim();
    const message = s.message.trim();
    if (!name) e.name = "Name is required";
    if (!email) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = "Enter a valid email";
    if (!phone) e.phone = "Phone is required";
    if (!s.eventDate) e.eventDate = "Event date is required";
    if (!s.eventType) e.eventType = "Event type is required";
    if (!s.servings) e.servings = "Number of servings is required";
    if (!message) e.message = "Message is required";
    return e;
  };

  async function onSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    setServerError(null);

    const v = validate(formData);
    setErrors(v);
    if (Object.values(v).some(Boolean)) return;

    // stable snapshot
    pendingRef.current = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      eventDate: formData.eventDate,
      eventType: formData.eventType,
      servings: formData.servings,
      location: formData.location,
      message: formData.message.trim(),
    };

    if (!SITE_KEY || !window.grecaptcha || widgetIdRef.current === null) {
      setSubmitting(true);
      await doSubmit("");
      return;
    }

    setSubmitting(true);
    window.grecaptcha.execute(widgetIdRef.current);
  }

  async function doSubmit(captchaToken: string) {
    const snap = pendingRef.current ?? formData;

    // Build FormData from the actual form element so the file input is included by name
    const fd = new FormData(formRef.current!);
    // Ensure all controlled fields are there
    fd.set("name", snap.name);
    fd.set("email", snap.email);
    fd.set("phone", snap.phone);
  fd.set("eventDate", snap.eventDate || "");
  fd.set("eventType", snap.eventType || "");
  fd.set("servings", snap.servings || "");
  fd.set("location", (snap as any).location || "");
  fd.set("message", snap.message);
    fd.set("captchaToken", captchaToken);
    fd.set("hp", "");

    // Also append from state (safe duplicate)
    files.forEach(({ file }) => fd.append("attachments", file, file.name));

    if (CLIENT_DEBUG) {
      const dbg: any[] = [];
      for (const [k, v] of (fd as any).entries()) {
        dbg.push([k, (v as any)?.name ? `File(${(v as any).name}, ${((v as any).size||0)} bytes)` : String(v).slice(0,80)]);
      }
      // eslint-disable-next-line no-console
      console.log("FormData entries:", dbg);
    }

    const r = await fetch("/api/contact", { method: "POST", body: fd });
    const text = await r.text();
    let j: any = {};
    try { j = JSON.parse(text); } catch {}

    if (!r.ok || !j?.ok) {
      const fe = (j?.fieldErrors ?? {}) as FieldErrors;
      setErrors(fe);
      setServerError(j?.error || `Server ${r.status}`);
      setSubmitting(false);
      window.grecaptcha?.reset(widgetIdRef.current!);
      // ⬇️ show error modal
      setResultOk(false);
      setResultMsg(j?.error ? String(j.error) : `There was a problem sending your inquiry (HTTP ${r.status}). Please try again.`);
      setShowResult(true);
      return;
    }

    // SUCCESS — hard reset
    toast({ title: "Thanks! Your inquiry was sent.", description: j.attachmentsCount ? `Attached files: ${j.attachmentsCount}` : undefined });
    // ⬇️ show success modal
    setResultOk(true);
    setResultMsg("Thanks! Your inquiry was sent successfully. We’ll get back to you within 24 hours.");
    setShowResult(true);
    setSubmitting(false);
    window.grecaptcha?.reset(widgetIdRef.current!);
    pendingRef.current = null;

  setFormData({ name:"", email:"", phone:"", eventDate:"", eventType:"", servings:"", location:"", message:"" });
    setFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setErrors({});
    setServerError(null);
  }

  const invalid = (f: keyof FieldErrors) => errors[f] ? "border-red-500 focus:ring-red-300" : "border-input focus:ring-pink-300";
  const labelClass = (f: keyof FieldErrors) => `text-primary ${errors[f] ? "font-bold text-red-700" : ""}`;

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-background to-muted/30 text-foreground">
      <div ref={recaptchaDivRef} style={{ display:"none" }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="mb-6">Let's Create Something Beautiful</h2>
          <p className="subheading max-w-3xl mx-auto mb-4">READY TO BRING YOUR VISION TO LIFE</p>
          <p className="max-w-3xl mx-auto leading-relaxed">
            Get in touch to discuss your custom cake needs. We're excited to be part of your special celebration.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left column (form) */}
          <div>
            <Card
              className="border card-shadow bg-gradient-to-b from-background/95 to-muted/40 text-foreground backdrop-blur-[1px]"
              style={{ borderColor: "var(--hero-h1)" }}
            >
              <CardHeader><CardTitle className="text-2xl font-heading text-primary">Request a Custom Quote</CardTitle></CardHeader>
              <CardContent className="p-8">
                <form ref={formRef} onSubmit={onSubmit} className="space-y-6" noValidate>
                  <input type="text" name="hp" className="hidden" tabIndex={-1} autoComplete="off" />

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className={labelClass("name")}>Name *</Label>
                      <Input id="name" name="name" value={formData.name} onChange={onChange("name")}
                             autoComplete="name" className={`mt-1 bg-transparent ${invalid("name")}`} aria-invalid={!!errors.name} required />
                      {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                    </div>
                    <div>
                      <Label htmlFor="email" className={labelClass("email")}>Email *</Label>
                      <Input id="email" name="email" type="email" value={formData.email} onChange={onChange("email")}
                             autoComplete="email" className={`mt-1 bg-transparent ${invalid("email")}`} aria-invalid={!!errors.email} required />
                      {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone" className={labelClass("phone")}>Phone *</Label>
                      <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={onChange("phone")}
                             autoComplete="tel" className={`mt-1 bg-transparent ${invalid("phone")}`} aria-invalid={!!errors.phone} required />

                      {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
                    </div>
                    <div>
                      <Label id="eventTypeLabel" className={labelClass("eventType")}>Event Type *</Label>
                      <Select value={formData.eventType} onValueChange={(v)=>setFormData(p=>({...p,eventType:v}))}>
                        <SelectTrigger
                          className="
                            mt-1
                            bg-transparent text-foreground
                            border border-foreground/20
                            hover:bg-[color-mix(in srgb, var(--foreground) 4%, transparent)]
                            focus:ring-0 focus-visible:ring-0
                          "
                          aria-labelledby="eventTypeLabel"
                        >

                          <SelectValue placeholder="Select event type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="wedding">Wedding</SelectItem>
                          <SelectItem value="birthday">Birthday</SelectItem>
                          <SelectItem value="anniversary">Anniversary</SelectItem>
                          <SelectItem value="corporate">Corporate</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                        </Select>
                        {errors.eventType && <p className="mt-1 text-sm text-red-600">{errors.eventType}</p>}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="eventDate" className="text-primary">Event Date *</Label>
                      <Input id="eventDate" type="date" value={formData.eventDate} onChange={onChange("eventDate")}
                             className="mt-1 fca-date bg-transparent" required />
                      {errors.eventDate && <p className="mt-1 text-sm text-red-600">{errors.eventDate}</p>}
                    </div>
                    <div>
                      <Label htmlFor="servings" className="text-primary">Number of Servings *</Label>
                      <Input id="servings" value={formData.servings} onChange={onChange("servings")}
                             placeholder="Approximate number (e.g., 100–150)" className="mt-1 bg-transparent" required />
                      {errors.servings && <p className="mt-1 text-sm text-red-600">{errors.servings}</p>}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="location" className="text-primary">Event/Delivery Location</Label>
                    <Input id="location" name="location" value={formData.location} onChange={onChange("location")}
                           placeholder="Venue or delivery address" className="mt-1 bg-transparent" />
                  </div>

                  {/* FILES */}
                  <div>
                    <Label htmlFor="attachments" className="text-primary">Attach image/file (optional)</Label>
                    <input
                      ref={fileInputRef}
                      id="attachments"
                      name="attachments"  // critical for FormData(formRef)
                      type="file"
                      multiple
                      accept="image/*,application/pdf,text/plain"
                      onChange={onFiles}
                      className="block w-full mt-1 text-sm"
                    />
                    {files.length>0 && (
                      <ul className="mt-2 space-y-1 text-sm">
                        {files.map(({file}, i)=>(
                          <li key={`${file.name}-${i}`} className="flex items-center justify-between">
                            <span className="truncate">{file.name} ({Math.max(1, Math.round(file.size/1024))} KB)</span>
                            <button type="button" onClick={()=>removeAttachmentAt(i)} className="text-red-600 hover:underline">remove</button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">Accepted: images/PDF/TXT, up to 5 MB each.</p>
                  </div>

                  {/* MESSAGE (required) */}
                  <div>
                    <Label htmlFor="message" className={labelClass("message")}>Tell us more about your theme! *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={onChange("message")}
                      placeholder="Describe your vision, flavors, design ideas, budget range, and any special requirements…"
                      className={`mt-1 min-h-[120px] bg-transparent ${invalid("message")}`}
                      aria-invalid={!!errors.message}
                      required
                    />
                    {errors.message && <p className="mt-1 text-sm text-red-600">{errors.message}</p>}
                  </div>

                  {/* Errors */}
                  {serverError && <p className="text-sm text-red-600" role="alert">{serverError}</p>}
                  {errors.captcha && <p className="text-sm text-red-600" role="alert">{errors.captcha}</p>}

                  <Button type="submit" size="lg" disabled={submitting}
                          className="w-full bg-[var(--contact-btn-bg)] hover:bg-[var(--contact-btn-hover-bg)] text-[var(--contact-btn-text)] hover:text-[var(--contact-btn-hover-text)] font-semibold py-4 disabled:opacity-60">
                    {submitting ? "Sending…" : "Send Inquiry"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Right column */}
          <div>
            <Card
              className="border card-shadow bg-gradient-to-b from-background/95 to-muted/40 text-foreground backdrop-blur-[1px]"
              style={{ borderColor: "var(--hero-h1)" }}
            >
              <CardHeader>
                <CardTitle className="text-2xl font-heading text-primary">Get in Touch</CardTitle>
              </CardHeader>
              <CardContent className="p-8">
                <h3 className="text-primary font-heading mb-3">Contact Information</h3>
                <ul className="space-y-3 text-base">
                  <li className="flex items-start"><span className="mt-2 mr-3 inline-block w-2 h-2 rounded-full bg-rose-300" /><a href="mailto:fcabakery@gmail.com" className="hover:underline">fcabakery@gmail.com</a></li>
                  <li className="flex items-start"><span className="mt-2 mr-3 inline-block w-2 h-2 rounded-full bg-rose-300" /><a href="tel:12165718440" className="hover:underline">216.571.8440</a></li>
                  <li className="flex items-start"><span className="mt-2 mr-3 inline-block w-2 h-2 rounded-full bg-rose-300" /><span>Chagrin Falls, OH 44022</span></li>
                  <li className="flex items-start"><span className="mt-2 mr-3 inline-block w-2 h-2 rounded-full bg-rose-300" /><span>Serving Bainbridge, Solon, Pepper Pike, Twinsburg, Mayfield and nearby areas</span></li>
                </ul>

                <h3 className="text-primary font-heading mt-8 mb-3">Business Hours</h3>
                <dl className="grid grid-cols-2 gap-y-2 text-sm sm:text-base">
                  <dt>Mon–Fri</dt><dd className="text-right text-muted-foreground">9:00 AM – 6:00 PM</dd>
                  <dt>Saturday</dt><dd className="text-right text-muted-foreground">10:00 AM – 4:00 PM</dd>
                  <dt>Sunday</dt><dd className="text-right text-muted-foreground">By Appointment Only</dd>
                </dl>

                <p className="text-xs text-muted-foreground mt-6">
                  We'll respond to your inquiry within 24 hours. For urgent requests, please call us directly.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      {/* result modal */}
      {showResult && (
        <div
          className="fixed inset-0 z-[10000] bg-black/40 flex items-center justify-center px-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowResult(false);
          }}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-background text-foreground border border-border shadow-xl p-6"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="mb-3 font-heading text-xl text-primary">
              {resultOk ? "Inquiry Sent" : "Send Failed"}
            </div>
            <p className="text-sm text-muted-foreground mb-6">{resultMsg}</p>
            <div className="flex justify-end">
              <Button
                onClick={() => setShowResult(false)}
                className="bg-[var(--about-btn-bg)] text-[var(--about-btn-text)] hover:bg-[var(--about-btn-hover-bg)] hover:text-[var(--about-btn-hover-text)]"
              >
                OK
              </Button>
            </div>
          </div>
        </div>
      )}

    </section>
  );
}
